import { useEffect, useRef, useState } from "react";
import { useFormStore } from "@/widgets/classic-form/form";
import type { HierarchicalFieldGroup, Hierarchies } from "@/entities/form-metadata";
import { useMetadataParamsContext } from "@/entities/form-metadata";
import type { IdHierarchy } from "@/entities/place";
import { useCategoryPlaceDataQuery } from "@/entities/place";
import { Grid } from "@/shared/components/Grid";
import { HierarchyInitializationProvider } from "./HierarchyInitializationContext";
import { HierarchyLevelItem } from "./HierarchyLevelItem";

interface HierarchyInstanceProps {
    hierarchy: Hierarchies;
}

const HierarchyInstance = ({ hierarchy }: HierarchyInstanceProps) => {
    const eventKey = `${hierarchy.relation}${hierarchy.eventType}`;
    const baseFormPath = `events.${eventKey}.instances.0`;

    // Read the gpid reactively so the component re-renders when the form is reset
    // (e.g. Clear Search), not only on selection. See TT-3953. The selector reads through
    // the typed FormValues shape, so a schema change surfaces as a type error here.
    const gpid = useFormStore((state) => state.values.events[eventKey]?.instances[0]?.gpid);

    // Child hierarchical levels (e.g. County) reveal via component-local state inside
    // HierarchyLevelItem, which formApi.reset() does not touch. When the gpid transitions
    // from a selected value back to empty (Clear Search), remount the whole subtree so
    // every nested level collapses back to its initial/default state.
    const [resetGeneration, setResetGeneration] = useState(0);
    // Seed with "" (empty = no selection) so the first observed non-empty -> empty
    // transition is what triggers the remount, regardless of store hydration timing.
    const previousGpidRef = useRef<string | undefined>("");

    useEffect(() => {
        const hadValue = Boolean(previousGpidRef.current);
        const isNowEmpty = !gpid;
        if (hadValue && isNowEmpty) {
            setResetGeneration((generation) => generation + 1);
        }
        previousGpidRef.current = gpid;
    }, [gpid]);

    const { data } = useCategoryPlaceDataQuery(gpid);

    const locationLevels: IdHierarchy = data?.[0]?.IdHierarchy ?? [];

    return (
        <HierarchyInitializationProvider key={resetGeneration}>
            {/* Each level wraps itself in a Grid.Field (see HierarchyLevelItem) so that
                nested levels are grid items too and share the container's spacing. */}
            {hierarchy.fields.map((field) => (
                <HierarchyLevelItem
                    key={field.label}
                    field={field}
                    hierarchy={hierarchy}
                    locationLevels={locationLevels}
                    testId={baseFormPath}
                />
            ))}
        </HierarchyInitializationProvider>
    );
};

interface HierarchicalGroupProps {
    fieldGroup: HierarchicalFieldGroup;
}
export const HierarchicalGroup = ({ fieldGroup }: HierarchicalGroupProps) => {
    const metadata = useMetadataParamsContext();
    const { hierarchies } = fieldGroup;

    if (metadata.formType !== "contentbased") return null;

    const hierarchyInstances = hierarchies.map((hierarchy) => (
        <HierarchyInstance key={`${hierarchy.relation}:${hierarchy.assertion}`} hierarchy={hierarchy} />
    ));
    return <Grid.Container>{hierarchyInstances}</Grid.Container>;
};
